import React from 'react'
import { CaretRight } from '@phosphor-icons/react'
import { Icon, LinkButton } from '@shopflo/ui'
import { classNames } from 'src/lib/utils/helpers'
import { PlanBadge } from 'src/lib/utils/ui/UpsellPlanBadge'
import type {
  UpsellHeaderNudgeProps,
  UpsellSidebarNudgeProps,
  UpsellUpgradeNudgeProps
} from 'src/lib/types/appsIntegrations'
import { IMAGE_CDN_URL } from 'src/lib/utils/constants'
import { useLocale } from 'src/lib/hooks/useLocal'

const SidebarNudge: React.FC<Omit<UpsellSidebarNudgeProps, 'variant'>> = ({ buttonConfig, textConfig, className }) => {
  const { t } = useLocale()
  const header = textConfig?.header?.trim() ? textConfig.header : t('upsell_upgrade_nudge_sidebar_header')
  const description = textConfig?.description?.trim()
    ? textConfig.description
    : t('upsell_upgrade_nudge_sidebar_description')
  const buttonText = buttonConfig.buttonText?.trim()
    ? buttonConfig.buttonText
    : t('upsell_upgrade_nudge_sidebar_button')

  return (
    <div
      className={classNames(
        'flex flex-col gap-2 rounded-xl border border-violet-200 bg-purple-50 p-4',
        className ?? ''
      )}
    >
      <PlanBadge plan='plus' className='w-fit' />

      <div className='flex flex-col gap-1 text-xs leading-[18px]'>
        <p className='font-semibold text-zinc-800'>{header}</p>
        <p className='text-zinc-500 font-medium'>{description}</p>
      </div>

      <LinkButton
        variant='default'
        size='sm'
        rightIcon={<Icon IconComponent={CaretRight} size='xxxs' iconProps={{ weight: 'bold' }} />}
        onClick={buttonConfig.onUpgradeClick}
        className='w-fit px-0 text-violet-700 hover:text-violet-800 text-xs font-semibold leading-[18px]'
      >
        {buttonText}
      </LinkButton>
    </div>
  )
}

const HeaderNudge: React.FC<Omit<UpsellHeaderNudgeProps, 'variant'>> = ({
  currentPlan,
  onViewPlansClick,
  textConfig,
  className,
  disabled
}) => {
  const { t } = useLocale()
  if (currentPlan !== 'lite') return null

  const header = textConfig?.header?.trim() ? textConfig.header : t('upsell_upgrade_nudge_header_header')
  const description = textConfig?.description?.trim()
    ? textConfig.description
    : t('upsell_upgrade_nudge_header_description')

  return (
    <div
      className={classNames(
        'flex items-center gap-3 rounded-2xl [box-shadow:0_0_0_1px_var(--Support-purple-200,#E9D4FF)_inset,0_0_0_0.5px_var(--Core-alpha-black-300,rgba(9,9,11,0.12)),0_2px_10px_-6px_var(--Core-alpha-black-600,rgba(9,9,11,0.48))] bg-white pr-4 pl-0 py-3',
        className ?? ''
      )}
    >
      <div>
        <img src={`${IMAGE_CDN_URL}/upsell-plus.svg`} alt='upsell-plus ' />
      </div>

      <div className='flex min-w-0 flex-1 flex-col gap-0.5'>
        <p className='text-text-sm-semibold text-zinc-800'>{header}</p>
        <p className='text-text-xs-regular text-zinc-500'>{description}</p>
      </div>

      <button type='button' onClick={onViewPlansClick} className='flex-shrink-0' disabled={Boolean(disabled)}>
        <span className='flex items-center gap-0.5 text-text-xs-semibold text-violet-700 whitespace-nowrap'>
          {t('upsell_upgrade_nudge_view_plans')}
          <Icon IconComponent={CaretRight} size='xxs' iconProps={{ weight: 'bold' }} />
        </span>
      </button>
    </div>
  )
}

const UpsellUpgradeNudge: React.FC<UpsellUpgradeNudgeProps> = props => {
  if (props.variant === 'header') {
    const { variant: _variant, ...headerProps } = props
    return <HeaderNudge {...headerProps} />
  }
  const { variant: _variant, ...sidebarProps } = props
  return <SidebarNudge {...sidebarProps} />
}

export default UpsellUpgradeNudge
